/**
 * Pure form-state helpers for the hotel checkout: profile pre-fill, defaults,
 * room ordering, the country list, and the live-total computation. No React, no
 * DOM — shared by the web checkout page and the React Native checkout screen
 * (ELARA-717).
 */

import type { RoomOptionModel as RoomOption } from '#models/RoomOptionModel.js';
import type { UserProfile } from '#models/UserProfileModel.js';
import type { HotelBookingRoom } from '@elara/domain/models/schemas/booking-schemas';

import type { CheckoutSnapshot, HotelCheckoutFormState, RoomFormState } from '#lib/hotel-checkout-types.js';

import { getCountriesList } from '#lib/country-utils.js';

// ISO 3166-1 alpha-2 list, computed once at module load — used by the
// "Country" select on the booking address.
export const COUNTRY_OPTIONS = getCountriesList();

export function defaultRoomFormEntry(): RoomFormState {
  return { extras: {}, bedTypePreference: undefined, smokingPreference: undefined, accessibilityNeeds: undefined };
}

/** Single-line address: street, city, state postal, country — empty fields are skipped. */
export function formatAddressLine(address: HotelCheckoutFormState['address']): string {
  const cityRegion = [address.city, address.postalCode].filter(Boolean).join(' ').trim();
  return [address.street, cityRegion, address.countryCode].filter(Boolean).join(', ');
}

/**
 * Best-effort split of a single display name into first/last for pre-fill.
 * Profile data should ideally come from `travelerProfile.firstName/lastName`;
 * this fallback is used when only `displayName` is populated.
 */
function splitDisplayName(displayName: string | undefined): { firstName: string; lastName: string } {
  const trimmed = (displayName ?? '').trim();
  // An account created without a name carries its email as the display name; that is not a name, and the
  // lead guest's name goes to the hotel on the reservation. Leave the field blank so the form asks.
  if (!trimmed || trimmed.includes('@')) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0]!, lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts.at(-1)! };
}

/**
 * Build the initial form state by seeding each room's editable fields from
 * the prepared rooms[] (which carry empty defaults for `selectedExtras`,
 * `bedTypePreference`, etc.) and pre-filling the lead guest / address from
 * the user's profile.
 */
export function initialFormState(
  snapshot: CheckoutSnapshot,
  profile: UserProfile | undefined,
  /** The verified phone from the Firebase Auth user record — preferred over the free-text profile phone. */
  verifiedPhone?: string,
): HotelCheckoutFormState {
  const rooms: HotelCheckoutFormState['rooms'] = {};
  for (const room of snapshot.rooms) {
    const extras: Record<string, number> = {};
    for (const ex of room.selectedExtras ?? []) {
      if (ex.extraId) extras[ex.extraId] = ex.quantity;
    }
    rooms[room.itineraryItemId] = {
      extras,
      bedTypePreference: room.bedTypePreference,
      smokingPreference: room.smokingPreference,
      accessibilityNeeds: room.accessibilityNeeds,
    };
  }

  const split = splitDisplayName(profile?.displayName);
  const profileFirst = profile?.travelerProfile?.firstName ?? split.firstName;
  const profileLast = profile?.travelerProfile?.lastName ?? split.lastName;
  const profileAddr = profile?.address;

  return {
    leadGuest: {
      firstName: profileFirst,
      lastName: profileLast,
      email: profile?.email ?? '',
      // Prefer the VERIFIED phone (Firebase Auth user record). `profile.phoneNumber` is the free-text
      // Firestore field and is usually empty, because phone verification writes to the auth record, not
      // the profile doc. `||` so an empty verified phone falls through to the profile value.
      phone: verifiedPhone || profile?.phoneNumber || '',
    },
    address: {
      street: profileAddr?.line1 ?? '',
      city: profileAddr?.city ?? '',
      postalCode: profileAddr?.postalCode ?? '',
      countryCode: profileAddr?.countryCode ?? '',
    },
    customerRequest: '',
    rooms,
  };
}

/**
 * Merge profile-derived defaults into an existing form state without
 * overriding fields the user has already filled. Called when the snapshot
 * shape changes (e.g. after a `price_changed` re-render) OR the profile
 * finishes loading after first render.
 */
export function mergeProfileIntoForm(
  prev: HotelCheckoutFormState,
  snapshot: CheckoutSnapshot,
  profile: UserProfile | undefined,
  verifiedPhone?: string,
): HotelCheckoutFormState {
  const rebuilt = initialFormState(snapshot, profile, verifiedPhone);
  return {
    leadGuest: {
      firstName: prev.leadGuest.firstName || rebuilt.leadGuest.firstName,
      lastName: prev.leadGuest.lastName || rebuilt.leadGuest.lastName,
      email: prev.leadGuest.email || rebuilt.leadGuest.email,
      phone: prev.leadGuest.phone || rebuilt.leadGuest.phone,
    },
    address: {
      street: prev.address.street || rebuilt.address.street,
      city: prev.address.city || rebuilt.address.city,
      postalCode: prev.address.postalCode || rebuilt.address.postalCode,
      countryCode: prev.address.countryCode || rebuilt.address.countryCode,
    },
    customerRequest: prev.customerRequest || rebuilt.customerRequest,
    rooms: rebuilt.rooms,
  };
}

/**
 * Sort rooms alphabetically by the room option's display name. Rooms whose
 * option hasn't loaded fall to the bottom under a 'Room' fallback. Stable
 * order — ties preserve the original document order.
 */
export function sortRoomsByName(
  rooms: HotelBookingRoom[],
  roomOptionsById: Map<string, RoomOption | undefined>,
): HotelBookingRoom[] {
  return rooms
    .map((room, index) => ({ room, index, name: roomOptionsById.get(room.roomOptionId)?.displayName ?? 'Room' }))
    .toSorted((a, b) => a.name.localeCompare(b.name) || a.index - b.index)
    .map(({ room }) => room);
}

/**
 * Compute the user-facing total for the summary. Sum is the per-room
 * `priceSnapshot` only — room extras (cribs, rollaways, etc.) are not part of
 * the prepaid Sabre booking; they're settled at the property and surface in
 * their own "Charged at the property" section in the summary.
 */
export function computeLiveTotal(snapshot: CheckoutSnapshot): { amount: number; currency: string } | undefined {
  if (snapshot.priceSnapshot.length === 0) return undefined;
  const currency = snapshot.priceSnapshot[0]?.totalCost.currency ?? 'USD';
  const amount = snapshot.priceSnapshot.reduce((acc, item) => acc + (item.totalCost.amount ?? 0), 0);
  return { amount, currency };
}
